// src/cases/ConfidisCaseShow.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Show,
  SimpleShowLayout,
  TabbedShowLayout,
  Tab,
  useRecordContext,
  EditButton,
} from "react-admin";
import {
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Button,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Refresh as RefreshIcon, PictureAsPdf as PdfIcon, Edit as EditIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../shared/firebase';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatDateForDisplay } from '../utils/dateUtils';
// pdf-lib y sus fuentes pesan 420 KB de los 2,6 MB del bundle del admin (el
// 16%), y solo hacen falta cuando alguien pulsa uno de los tres botones de
// PDF. Con import() dinamico webpack los saca a un trozo aparte que no se
// descarga al abrir el panel. Las tres llamadas ya estaban dentro de un
// try/catch con su spinner, asi que si el trozo no llega se ve el mismo
// mensaje de error de siempre.
import {
  formatCurrency,
  getCreditSituationColor,
  getCreditTypeBreakdown,
  getDisplayAge,
  normalizeCreditSituation,
  parseNumericValue,
} from '../utils/caseDisplayUtils';
import CustomEditButton from '../components/CustomEditButton';
import AdminCreditInformation from '../components/AdminCreditInformation';
import ApiSendButtons from '../components/ApiSendButtons';
import CofidisPrecalStatus from '../components/CofidisPrecalStatus';
import PublicidadConcursalButton from '../components/PublicidadConcursalButton';
import { DETAIL_CACHE_MAX_AGE_MS, loadDocWithCache } from '../utils/firestoreCache';
import MortgageFreeNotice from './MortgageFreeNotice';

// Visibilidad solicitada por Operaciones; no se hereda del rol admin.
const canShowSignatureLink = (email) => [
  'andrea.cornejo@easydebt.es',
  'diego.catter@easydebt.es',
  'sandy.hernandez@easydebt.es',
  'alejandro.martinez@easydebt.es',
].includes(String(email || '').trim().toLowerCase());


// Section component for better organization
const Section = ({ title, children }) => (
  <Box mb={3}>
    <Typography variant="h6" color="primary" gutterBottom>
      {title}
    </Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Box>
);

const InlineBankGroupField = ({ caseId, initialValue }) => {
  const [value, setValue] = useState(initialValue || '');
  const [draft, setDraft] = useState(initialValue || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setValue(initialValue || '');
    setDraft(initialValue || '');
  }, [initialValue]);

  const handleSave = async () => {
    const nextValue = draft.trim();

    if (nextValue === value.trim()) {
      setIsEditing(false);
      return;
    }

    if (!caseId) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'cofidisCases', caseId), { bankGroup: nextValue });
      setValue(nextValue);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving bankGroup:', err);
      setError('No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value);
    setIsEditing(false);
    setError(null);
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          disabled={saving}
          style={{
            border: '1px solid #1976d2',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '0.95rem',
            outline: 'none',
            minWidth: '160px',
          }}
        />
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? '...' : 'Guardar'}
        </Button>
        <Button size="small" onClick={handleCancel} disabled={saving}>
          Cancelar
        </Button>
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </Box>
    );
  }

  return (
    <Box
      onClick={() => setIsEditing(true)}
      title="Clic para editar"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        '&:hover .edit-pencil': { opacity: 1 },
      }}
    >
      <Typography variant="body1">{value || '-'}</Typography>
      <EditIcon
        className="edit-pencil"
        fontSize="small"
        sx={{ color: '#1976d2', opacity: 0.5, transition: 'opacity 0.15s' }}
      />
    </Box>
  );
};

// Holder information component
const HolderInfo = ({ title, holderData }) => {
  const age = getDisplayAge(holderData);

  return (
  <Section title={title}>
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Nombre</Typography>
        <Typography variant="body1">{holderData?.nombre || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Apellidos</Typography>
        <Typography variant="body1">{holderData?.apellidos || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">DNI</Typography>
        <Typography variant="body1">{holderData?.dni || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Fecha de nacimiento</Typography>
        <Typography variant="body1">
          {holderData?.fechaNacimiento ? new Date(holderData.fechaNacimiento).toLocaleDateString('es-ES') : '-'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Edad</Typography>
        <Typography
          variant="body1"
          sx={{
            color: age > 65 ? 'error.main' : 'text.primary',
            fontWeight: age > 65 ? 600 : 400,
          }}
        >
          {age !== null ? `${age} Años` : '-'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Nacionalidad</Typography>
        <Typography variant="body1">{holderData?.nacionalidad || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Dirección postal</Typography>
        <Typography variant="body1">{holderData?.direccionPostal || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Población</Typography>
        <Typography variant="body1">{holderData?.poblacion || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Provincia</Typography>
        <Typography variant="body1">{holderData?.provincia || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Código Postal</Typography>
        <Typography variant="body1">{holderData?.codigoPostal || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Teléfono fijo</Typography>
        <Typography variant="body1">{holderData?.telefonoFijo || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Móvil</Typography>
        <Typography variant="body1">{holderData?.movil || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Email</Typography>
        <Typography variant="body1">{holderData?.email || '-'}</Typography>
      </Grid>
    </Grid>
  </Section>
  );
};

// Professional data component
// ── Red-flag validations (Diego's request) ──────────────────────────────
const isSalarioLow = (v) => {
  if (v == null || v === '') return false;
  const n = Number(String(v).replace(/[^0-9.,]/g, '').replace(',', '.'));
  return !isNaN(n) && n < 1400;
};

const parseFlexibleDate = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/); // DD/MM/YYYY
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

// True if the labor start date is more recent than 3 months ago.
const isAntiguedadShort = (v) => {
  const d = parseFlexibleDate(v);
  if (!d) return false;
  const threshold = new Date();
  threshold.setMonth(threshold.getMonth() - 3);
  return d > threshold;
};

const redFlagSx = { color: 'error.main', fontWeight: 700 };

const ProfessionalData = ({ title, professionalData }) => (
  <Section title={title}>
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Profesión</Typography>
        <Typography variant="body1">{professionalData?.profesion || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Salario neto mensual</Typography>
        <Typography variant="body1" sx={isSalarioLow(professionalData?.salarioNetoMensual) ? redFlagSx : undefined}>
          {professionalData?.salarioNetoMensual ? `${professionalData.salarioNetoMensual.toLocaleString('es-ES')} €` : '-'}
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Número de pagas</Typography>
        <Typography variant="body1">{professionalData?.numeroPagas || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Tipo de contrato</Typography>
        <Typography variant="body1">{professionalData?.tipoContrato || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Antigüedad laboral</Typography>
        <Typography variant="body1" sx={isAntiguedadShort(professionalData?.antiguedadLaboral) ? redFlagSx : undefined}>
          {professionalData?.antiguedadLaboral || '-'}
        </Typography>
      </Grid>
    </Grid>
  </Section>
);

// Housing data component
const HousingData = ({ gastosVivienda }) => {
  // Map the tipoVivienda value to display text
  const getTipoViviendaText = (tipoVivienda) => {
    switch (tipoVivienda) {
      case 'propiedadSinHipoteca':
        return 'Propiedad sin hipoteca';
      case 'propiedadConHipoteca':
        return 'Propiedad con hipoteca';
      case 'alquiler':
        return 'Alquiler';
      case 'otros':
        return 'Otros';
      default:
        return 'No especificado';
    }
  };

  const tipoViviendaText = getTipoViviendaText(gastosVivienda?.tipoVivienda);

  return (
    <Section title="DATOS DE VIVIENDA">
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Tipo de Vivienda
          </Typography>
          <Typography variant="body1" sx={gastosVivienda?.tipoVivienda === 'otros' ? redFlagSx : undefined}>
            {tipoViviendaText}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Importe Alquiler
          </Typography>
          <Typography variant="body1">
            {gastosVivienda?.importeAlquiler ? `${gastosVivienda.importeAlquiler.toLocaleString('es-ES')} €` : '-'}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Importe Hipoteca
          </Typography>
          <Typography variant="body1">
            {gastosVivienda?.importeHipoteca ? `${gastosVivienda.importeHipoteca.toLocaleString('es-ES')} €` : '-'}
          </Typography>
        </Grid>
      </Grid>
    </Section>
  );
};

// Car data component
const CarData = ({ datosCoche }) => {
  return (
    <Section title="DATOS DE COCHE">
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Matrícula
          </Typography>
          <Typography variant="body1">
            {datosCoche?.matricula || '-'}
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Kilometraje
          </Typography>
          <Typography variant="body1">
            {datosCoche?.kilometraje ? `${datosCoche.kilometraje.toLocaleString('es-ES')} km` : '-'}
          </Typography>
        </Grid>
      </Grid>
    </Section>
  );
};

// Other data component
const OtherData = ({ otrosDatos }) => (
  <Section title="OTROS DATOS">
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Estado civil</Typography>
        <Typography variant="body1">{otrosDatos?.estadoCivil || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Nº hijos a su cargo</Typography>
        <Typography variant="body1">{otrosDatos?.numeroHijos || '0'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Otros créditos/préstamos</Typography>
        <Typography variant="body1">{otrosDatos?.otrosCreditosPrestamos || '-'}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" color="textSecondary">Importe mensual</Typography>
        <Typography variant="body1">
          {otrosDatos?.importeMensual ? `${otrosDatos.importeMensual.toLocaleString('es-ES')} €` : '-'}
        </Typography>
      </Grid>
    </Grid>
  </Section>
);

const FinancialInformation = ({ informacionFinanciera }) => {
  if (!informacionFinanciera) return null;

  const labels = {
    'con-ahorros': 'Tenemos ahorros para la hipoteca',
    'sin-ahorros': 'Evaluar hipoteca sin ahorros, más gastos',
    'con-y-sin-ahorros': 'Evaluar hipoteca con y sin ahorros',
  };

  return (
    <Section title="INFORMACIÓN FINANCIERA">
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" color="textSecondary">Preferencia para la hipoteca</Typography>
          <Typography variant="body1">{labels[informacionFinanciera.opcion] || informacionFinanciera.opcion || '-'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="textSecondary">Ahorros disponibles</Typography>
          <Typography variant="body1">
            {informacionFinanciera.importeAhorros
              ? `${Number(informacionFinanciera.importeAhorros).toLocaleString('es-ES')} €`
              : '-'}
          </Typography>
        </Grid>
      </Grid>
    </Section>
  );
};

// Credit Information component
const CreditInformation = ({ creditos }) => {
  if (!creditos || creditos.length === 0) {
    return (
      <Section title="INFORMACIÓN DE CRÉDITOS">
        <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          No hay información de créditos disponible
        </Typography>
      </Section>
    );
  }

  const normalizedCreditos = creditos.map((credit) => ({
    ...credit,
    situacion: normalizeCreditSituation(credit.situacion),
  }));

  const totalCapitalPendiente = normalizedCreditos.reduce(
    (sum, credit) => sum + parseNumericValue(credit.capitalPendiente),
    0
  );
  const totalCuotaMensual = normalizedCreditos.reduce(
    (sum, credit) => sum + parseNumericValue(credit.cuotaMensual),
    0
  );
  const creditTypeBreakdown = getCreditTypeBreakdown(normalizedCreditos);

  return (
    <Section title="INFORMACIÓN DE CRÉDITOS">
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--ed-soft, #f8f9fa)' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Banco / Financiera</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tipo Crédito</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Capital Pendiente</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Cuota Mensual</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Fecha Solicitud</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Situación</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {normalizedCreditos.map((credit, index) => (
              <TableRow key={credit.id || index} sx={{ '&:nth-of-type(even)': { backgroundColor: 'var(--ed-soft, #f8f9fa)' } }}>
                <TableCell>{credit.banco || '-'}</TableCell>
                <TableCell>{credit.tipoCredito || '-'}</TableCell>
                <TableCell>
                  {credit.capitalPendiente ? formatCurrency(credit.capitalPendiente) : '-'}
                </TableCell>
                <TableCell>
                  {credit.cuotaMensual ? formatCurrency(credit.cuotaMensual) : '-'}
                </TableCell>
                <TableCell>{credit.fechaSolicitud || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={credit.situacion || '-'}
                    size="small"
                    color={getCreditSituationColor(credit.situacion)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Credit Summary */}
      <Card sx={{ mt: 2, bgcolor: '#e3f2fd' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Resumen de Créditos
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Total de Créditos: <strong>{creditos.length}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Capital Total Pendiente: <strong>
                  {formatCurrency(totalCapitalPendiente)}
                </strong>
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">
                Cuota Mensual Total: <strong>
                  {formatCurrency(totalCuotaMensual)}
                </strong>
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {creditTypeBreakdown.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Capital pendiente por tipo de crédito
          </Typography>
          <Grid container spacing={2}>
            {creditTypeBreakdown.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.key}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {item.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: item.highlightInRed ? 'error.main' : 'text.primary',
                    }}
                  >
                    {formatCurrency(item.total)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Section>
  );
};

export const CofidisCaseShow = () => {
  const record = useRecordContext();
  const [showSignatureLink, setShowSignatureLink] = useState(() => canShowSignatureLink(auth.currentUser?.email));
  useEffect(() => onAuthStateChanged(auth, (user) => {
    setShowSignatureLink(canShowSignatureLink(user?.email));
  }), []);

  // Detect if we're in admin view
  const isAdminView = process.env.REACT_APP_VIEW === 'admin' || !process.env.REACT_APP_VIEW;
  const { id } = useParams();
  const [fetchedRecord, setFetchedRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Cuando se leyo el documento por ultima vez, para no leerlo dos veces
  // seguidas cuando focus y visibilitychange saltan a la vez.
  const ultimaRecarga = useRef(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [tcPdfLoading, setTcPdfLoading] = useState(false);
  const [tcPdfError, setTcPdfError] = useState(null);
  const [summaryPdfLoading, setSummaryPdfLoading] = useState(false);
  const [signatureUrlLoading, setSignatureUrlLoading] = useState(false);
  const [signatureUrlSuccess, setSignatureUrlSuccess] = useState(null);
  const detailCacheKey = id ? `cofidisCases:detail:${id}` : null;

  // Function to fetch record
  const fetchRecord = useCallback(async ({ forceServer = false } = {}) => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const db = getFirestore();
      const docRef = doc(db, 'cofidisCases', id);
      const applySnapshot = (docSnap) => {
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          setFetchedRecord(data);
          setError(null);
        } else {
          setFetchedRecord(null);
          setError('Document not found');
        }
        setLoading(false);
      };

      await loadDocWithCache({
        docRef,
        cacheKey: detailCacheKey,
        maxAgeMs: DETAIL_CACHE_MAX_AGE_MS,
        forceServer,
        onCache: applySnapshot,
        onServer: applySnapshot,
      });
    } catch (err) {
      console.error('CofidisCaseShow - error fetching record:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [detailCacheKey, id]);

  // Load from cache first and only refresh from the server when the cache is stale.
  useEffect(() => {
    fetchRecord();
  }, [fetchRecord, refreshKey]);

  // Auto-refresh when component gains focus (returning from edit)
  useEffect(() => {
    // Los dos avisos hacen falta: visibilitychange cubre cambiar de pestana y
    // focus cubre cambiar de aplicacion con la pestana a la vista. Lo que
    // pasaba es que al volver saltan LOS DOS, asi que el documento se leia dos
    // veces seguidas: doble lectura y el parpadeo de la ficha recargandose.
    // Se ignora la segunda si llega pegada a la primera.
    const MARGEN_MS = 1000;
    const recargar = () => {
      if (!id) return;
      const ahora = Date.now();
      if (ahora - ultimaRecarga.current < MARGEN_MS) return;
      ultimaRecarga.current = ahora;
      fetchRecord();
    };

    const handleFocus = () => recargar();
    const handleVisibilityChange = () => { if (!document.hidden) recargar(); };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchRecord, id]);

  // Auto-refresh when URL changes (navigation back from edit)
  useEffect(() => {
    const handlePopState = () => {
      if (id) {
        // Small delay to ensure the navigation is complete
        setTimeout(() => fetchRecord(), 100);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [fetchRecord, id]);



  // Enhanced refresh function that forces immediate fetch
  const forceRefresh = () => {
    setRefreshKey(prev => prev + 1);
    fetchRecord({ forceServer: true });
  };

  // PDF filling function
  const handleFillPDF = async () => {
    console.log('handleFillPDF called, displayRecord:', displayRecord);
    if (!displayRecord) {
      console.log('No displayRecord available');
      return;
    }

    try {
      setPdfLoading(true);
      setPdfError(null);
      console.log('Starting PDF generation...');
      const { fillCofidisPDF } = await import('../utils/pdfUtils');
      await fillCofidisPDF(displayRecord);
      console.log('PDF generation completed successfully');
    } catch (error) {
      console.error('Error filling PDF:', error);
      setPdfError('Error al generar el PDF. Por favor, inténtelo de nuevo.');
    } finally {
      setPdfLoading(false);
    }
  };

  // TC PDF filling function
  const handleFillTCPDF = async () => {
    console.log('handleFillTCPDF called, displayRecord:', displayRecord);
    if (!displayRecord) {
      console.log('No displayRecord available');
      return;
    }

    // Debug: Log the IP and time fields specifically
    console.log('TC PDF - Checking IP and time fields:', {
      userIP: displayRecord.userIP,
      madridCreateTime: displayRecord.madridCreateTime,
      madridCreateHour: displayRecord.madridCreateHour,
      madridCreateMinutes: displayRecord.madridCreateMinutes,
      registeredDate: displayRecord.registeredDate
    });

    try {
      setTcPdfLoading(true);
      setTcPdfError(null);
      console.log('Starting TC PDF generation...');
      const { fillTCPDF } = await import('../utils/pdfUtils');
      await fillTCPDF(displayRecord);
      console.log('TC PDF generation completed successfully');
    } catch (error) {
      console.error('Error generating TC PDF:', error);
      setTcPdfError('Error al generar el PDF TC. Por favor, inténtelo de nuevo.');
    } finally {
      setTcPdfLoading(false);
    }
  };

  const handleDownloadSummaryPDF = async () => {
    if (!displayRecord) {
      return;
    }

    try {
      setSummaryPdfLoading(true);
      setPdfError(null);
      const { generateCaseSummaryPDF } = await import('../utils/pdfUtils');
      await generateCaseSummaryPDF(displayRecord);
    } catch (error) {
      console.error('Error generating summary PDF:', error);
      setPdfError('Error al generar el resumen PDF. Por favor, inténtelo de nuevo.');
    } finally {
      setSummaryPdfLoading(false);
    }
  };

  // Signature URL generation function
  const handleGenerateSignatureUrl = async () => {
    if (!canShowSignatureLink(auth.currentUser?.email) || !displayRecord || !displayRecord.id) return;

    try {
      setSignatureUrlLoading(true);
      setPdfError(null);
      setSignatureUrlSuccess(null);

      // Con este enlace se ve el DNI y los créditos del cliente sin iniciar
      // sesión, así que el token es la única protección. Math.random no es
      // criptográfico (su estado se puede reconstruir a partir de unas pocas
      // salidas); crypto.getRandomValues sí. Auditoría del 05/09.
      const bytes = new Uint8Array(24);
      window.crypto.getRandomValues(bytes);
      const token = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

      // Caducidad a 30 días: suficiente margen para que el cliente firme sin
      // que el enlace quede abierto para siempre si se reenvía o se filtra.
      const caduca = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      const db = getFirestore();
      const docRef = doc(db, 'cofidisCases', displayRecord.id);
      await updateDoc(docRef, { signatureToken: token, signatureTokenExpiresAt: caduca });

      const signatureUrl = `https://solicitud.easydebt.es/?action=sign&caseId=${displayRecord.id}&token=${token}`;
      await navigator.clipboard.writeText(signatureUrl);

      setSignatureUrlSuccess('URL generada y copiada al portapapeles exitosamente.');
      forceRefresh();
    } catch (error) {
      console.error('Error generating signature URL:', error);
      setPdfError('Error al generar enlace de firma. Por favor, inténtelo de nuevo.');
    } finally {
      setSignatureUrlLoading(false);
      setTimeout(() => { setSignatureUrlSuccess(null); }, 5000);
    }
  };


  // Use fetched record if context record is not available
  const displayRecord = record || fetchedRecord;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (!displayRecord) {
    return <div>No record found. Please check the URL and try again.</div>;
  }

  return (
    <Show>
      <MortgageFreeNotice record={displayRecord} />
      {/* PDF Buttons - Always visible at the top */}
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PdfIcon />}
            onClick={handleFillPDF}
            disabled={pdfLoading || !displayRecord}
            sx={{
              backgroundColor: '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {pdfLoading ? 'Generando PDF...' : 'Generar PDF'}
          </Button>
          <IconButton onClick={forceRefresh} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PdfIcon />}
          onClick={handleFillTCPDF}
          disabled={tcPdfLoading || !displayRecord}
          sx={{
            backgroundColor: '#9c27b0',
            color: 'white',
            '&:hover': {
              backgroundColor: '#7b1fa2'
            }
          }}
        >
          {tcPdfLoading ? 'Generando TC...' : 'Generar TC'}
        </Button>
        {showSignatureLink && <Button
          variant="contained"
          color="secondary"
          startIcon={<PdfIcon />}
          onClick={handleGenerateSignatureUrl}
          disabled={signatureUrlLoading || !displayRecord}
          sx={{
            backgroundColor: '#9c27b0',
            color: 'white',
            '&:hover': {
              backgroundColor: '#7b1fa2'
            }
          }}
        >
          {signatureUrlLoading ? 'Generando...' : 'Obtener Enlace Firma'}
        </Button>}
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PdfIcon />}
          onClick={handleDownloadSummaryPDF}
          disabled={summaryPdfLoading || !displayRecord}
          sx={{ alignSelf: 'stretch' }}
        >
          {summaryPdfLoading ? 'Generando resumen...' : 'Descargar Resumen PDF'}
        </Button>
      </Box>

      {pdfError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pdfError}
        </Alert>
      )}
      {tcPdfError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {tcPdfError}
        </Alert>
      )}
      {signatureUrlSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {signatureUrlSuccess}
        </Alert>
      )}

      {/* Public insolvency registry check (client-side, opens official site) */}
      <Box sx={{ mb: 2 }}>
        <PublicidadConcursalButton dni={displayRecord?.primerTitular?.dni} />
      </Box>

      {/* Affiliate API integrations — manual send */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid var(--ed-line, #e9ecef)', borderRadius: 2, backgroundColor: 'var(--ed-soft, #f8f9fa)' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--ed-text-2, #495057)', mb: 1.5, letterSpacing: '0.5px' }}>
          INTEGRACIONES API
        </Typography>
        {displayRecord && (
          <ApiSendButtons
            collection="cofidisCases"
            caseId={displayRecord.id}
            providers={['deudafix', 'unifye', 'witme']}
            initialSends={displayRecord.apiSends}
          />
        )}
        {displayRecord && <CofidisPrecalStatus record={displayRecord} />}
      </Box>

      <TabbedShowLayout>
        <Tab label="Información General" path="">
          <SimpleShowLayout>
            <Section title="INFORMACIÓN DEL CASO">
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">Número de Caso</Typography>
                  <Typography variant="h6" color="primary">{displayRecord.caseNumber}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">Fecha de Registro</Typography>
                  <Typography variant="body1">
                    {formatDateForDisplay(displayRecord.registeredDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">Estado</Typography>
                  <Chip
                    label={displayRecord.status || 'En Revisión'}
                    color={
                      displayRecord.status === 'Aprobado' ? 'success' :
                        displayRecord.status === 'Rechazado' ? 'error' : 'warning'
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">Grupo Bancario</Typography>
                  <InlineBankGroupField
                    caseId={displayRecord.id}
                    initialValue={
                      !displayRecord.bankGroup || displayRecord.bankGroup === 'COFIDIS'
                        ? 'Banco'
                        : displayRecord.bankGroup
                    }
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="textSecondary">IBAN (1er titular)</Typography>
                  <Typography variant="body1">{displayRecord.iban || '-'}</Typography>
                </Grid>
                {displayRecord.ibanSegundo && (
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" color="textSecondary">IBAN (2º titular)</Typography>
                    <Typography variant="body1">{displayRecord.ibanSegundo}</Typography>
                  </Grid>
                )}
              </Grid>
            </Section>

            <HolderInfo title="DATOS DEL PRIMER TITULAR" holderData={displayRecord.primerTitular} />

            {displayRecord.segundoTitular && displayRecord.segundoTitular.nombre && (
              <HolderInfo title="DATOS DEL SEGUNDO TITULAR" holderData={displayRecord.segundoTitular} />
            )}

            <ProfessionalData title="DATOS PROFESIONALES (PRIMER TITULAR)" professionalData={displayRecord.primerTitular} />

            {displayRecord.segundoTitular && displayRecord.segundoTitular.profesion && (
              <ProfessionalData title="DATOS PROFESIONALES (SEGUNDO TITULAR)" professionalData={displayRecord.segundoTitular} />
            )}

            <HousingData gastosVivienda={displayRecord.gastosVivienda} />
            <FinancialInformation informacionFinanciera={displayRecord.informacionFinanciera} />
            <CarData datosCoche={displayRecord.datosCoche} />
            <OtherData otrosDatos={displayRecord.otrosDatos} />

            {displayRecord.note && (
              <Section title="NOTAS">
                <Typography variant="body1">{displayRecord.note}</Typography>
              </Section>
            )}
          </SimpleShowLayout>
        </Tab>

        <Tab label="Información de Créditos" path="creditos">
          <SimpleShowLayout>
            {isAdminView ? (
              <AdminCreditInformation creditos={displayRecord.creditos} record={displayRecord} />
            ) : (
              <CreditInformation creditos={displayRecord.creditos} />
            )}
          </SimpleShowLayout>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
}; 
